import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ReachStakeholder, AdoptionCurve } from '../../../services/reachService';

const COLOURS = ['#00c7c4', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316', '#3b82f6'];
const ADOPTION_CURVES: { value: AdoptionCurve; label: string }[] = [
  { value: 'innovator', label: 'Innovator' },
  { value: 'early_adopter', label: 'Early Adopter' },
  { value: 'early_majority', label: 'Early Majority' },
  { value: 'late_majority', label: 'Late Majority' },
  { value: 'sceptic', label: 'Sceptic' },
];

interface Props {
  initial?: Partial<ReachStakeholder>;
  onSave: (data: Omit<ReachStakeholder, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onClose: () => void;
}

export const StakeholderForm: React.FC<Props> = ({ initial, onSave, onClose }) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [role, setRole] = useState(initial?.role ?? '');
  const [department, setDepartment] = useState(initial?.department ?? '');
  const [color, setColor] = useState(initial?.color ?? COLOURS[0]);
  const [adoptionCurve, setAdoptionCurve] = useState<AdoptionCurve>(initial?.adoptionCurve ?? 'early_majority');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), role: role.trim(), department: department.trim(), color, adoptionCurve, x: initial?.x ?? 0.5, y: initial?.y ?? 0.5 });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <h2 className="text-sm font-bold text-navy">{initial?.name ? 'Edit Stakeholder' : 'Add Stakeholder'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-navy transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">Name <span className="text-red-500">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">Role</label>
              <input value={role} onChange={e => setRole(e.target.value)} placeholder="Job title"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">Department</label>
              <input value={department} onChange={e => setDepartment(e.target.value)} placeholder="Team / function"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">Adoption Curve Position</label>
            <select value={adoptionCurve} onChange={e => setAdoptionCurve(e.target.value as AdoptionCurve)}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
              {ADOPTION_CURVES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">Colour</label>
            <div className="flex gap-2">
              {COLOURS.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${color === c ? 'border-navy scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit"
              className="rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold transition-colors">
              {initial?.name ? 'Save Changes' : 'Add Stakeholder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
